import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RefreshCw, Play, Pause, Activity, AlertCircle } from 'lucide-react'
import { baliseService } from '../services/baliseService'

// BaliseSyncControl — Tailwind port of the legacy Ionic
// BaliseSyncControl widget. Lives inside the BaliseManagement detail
// modal as the only interactive element on what is otherwise a strictly
// read-only specs view. Sync state is a separate concern from
// assignment state — the operator should be able to pause/resume sync
// without unassigning the balise from a customer.
//
// The Java resource @Path is `/balise-sync` (Jersey mounted at `/api/*`),
// so the wire path is `/api/balise-sync/...`. If the backend is
// unreachable the widget degrades to a read-only "status unavailable"
// card instead of crashing the modal.

interface SyncStatusPayload {
  lockCode?: string
  syncStatus?: string
  assignmentStatus?: string
  isAssigned?: boolean
  hasCustomer?: boolean
  syncActivatedAt?: string
  syncActivatedBy?: string
  syncDeactivatedAt?: string
  syncDeactivatedBy?: string
  syncMaintainedAt?: string
  syncMaintainedBy?: string
  // The Java endpoint wraps the payload in { success, data }, but other
  // shapes are possible — we read defensively.
  [k: string]: unknown
}

interface BaliseSyncControlProps {
  lockCode: string
  showMessage: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

function badgeClassFor(status?: string): string {
  const u = (status || '').toUpperCase()
  if (u === 'ACTIVE')      return 'bg-emerald-100 text-emerald-800 border-emerald-200'
  if (u === 'INACTIVE')    return 'bg-gray-100 text-gray-800 border-gray-200'
  if (u === 'MAINTENANCE') return 'bg-amber-100 text-amber-800 border-amber-200'
  return 'bg-gray-100 text-gray-700 border-gray-200'
}

const BaliseSyncControl: React.FC<BaliseSyncControlProps> = ({ lockCode, showMessage }) => {
  const { t } = useTranslation()
  const [status, setStatus] = useState<SyncStatusPayload | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!lockCode) return
    setLoading(true)
    setError(null)
    try {
      const raw = await baliseService.getSyncStatus(lockCode)
      const data = (raw?.data ?? raw) as SyncStatusPayload
      setStatus(data || null)
    } catch (e: any) {
      setError(e?.message || 'Sync status unavailable')
      setStatus(null)
    } finally {
      setLoading(false)
    }
  }, [lockCode])

  useEffect(() => { load() }, [load])

  const runAction = useCallback(async (action: 'activate' | 'deactivate' | 'maintain') => {
    if (busy) return
    setBusy(true)
    try {
      await baliseService.syncAction(lockCode, action)
      showMessage(t('balise_sync_action_success'), 'success')
      load()
    } catch (e: any) {
      showMessage(e?.message || t('balise_sync_action_failed'), 'error')
    } finally {
      setBusy(false)
    }
  }, [busy, lockCode, load, showMessage, t])

  const syncStatus = (status?.syncStatus || '').toUpperCase()
  const isActive = syncStatus === 'ACTIVE'
  const isUnavailable = !!error && !status

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gradient-to-br from-blue-50/50 to-white">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-blue-600" />
          <h3 className="text-sm font-semibold text-gray-900">
            {t('sync_widget_title', { defaultValue: 'Data sync' })}
          </h3>
          {!isUnavailable && (
            <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide ${badgeClassFor(syncStatus)}`}>
              {syncStatus || t('balise_status_unknown')}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading || busy}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
          title={t('sync_widget_refresh', { defaultValue: 'Refresh sync status' }) as string}
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          {t('sync_widget_refresh_short', { defaultValue: 'Refresh' })}
        </button>
      </div>

      {isUnavailable ? (
        <div className="mt-2 flex items-start gap-2 text-[11px] text-amber-700">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div>{t('sync_widget_unavailable', { defaultValue: 'Sync status is unavailable for this balise.' })}</div>
        </div>
      ) : (
        <>
          {status?.syncActivatedAt && (
            <div className="mt-1 text-[11px] text-gray-500">
              {t('sync_widget_activated_at', { defaultValue: 'Activated' })}: {new Date(status.syncActivatedAt).toLocaleString()}
              {status.syncActivatedBy ? ` · ${status.syncActivatedBy}` : ''}
            </div>
          )}
          {status?.syncMaintainedAt && (
            <div className="text-[11px] text-gray-500">
              {t('sync_widget_maintained_at', { defaultValue: 'Maintained' })}: {new Date(status.syncMaintainedAt).toLocaleString()}
              {status.syncMaintainedBy ? ` · ${status.syncMaintainedBy}` : ''}
            </div>
          )}
        </>
      )}

      <div className="mt-2 flex flex-wrap gap-2">
        {!isActive ? (
          <button
            type="button"
            disabled={busy || isUnavailable}
            onClick={() => runAction('activate')}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <Play className="w-3 h-3" />
            {t('balise_sync_activate')}
          </button>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => runAction('deactivate')}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
          >
            <Pause className="w-3 h-3" />
            {t('balise_sync_deactivate')}
          </button>
        )}
        <button
          type="button"
          disabled={busy || isUnavailable}
          onClick={() => runAction('maintain')}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <Activity className="w-3 h-3" />
          {t('balise_sync_maintain')}
        </button>
      </div>
    </div>
  )
}

export default BaliseSyncControl
